import React, { useEffect, useRef, useState, useCallback } from 'react';
import Icon from 'components/AppIcon';
import { analyzeAudioBuffer, parseLyrics, drawCaptionOverlay, getCurrentLyricLine, extractVisualKeywords, formatDurationLabel,  } from 'utils/videoUtils';
import { buildSceneTimeline, preloadSceneImages } from 'utils/sceneBuilder';
import { useAuth } from '../../../contexts/AuthContext';
import { deductCoinsForGeneration, refundCoinsForFailedJob, queueGenerationJob, updateJobProgress, analyzeMusicForScenes, generateSceneImageBatch, deductVideoSeconds, refundVideoSeconds,  } from '../../../services/generationPipeline';
import { runGenerationPreflight, completeGuardSetup, releaseGuardResources } from '../../../services/generationGuard';

const VISUAL_STYLES = [
  { id: 'particles', label: 'Particles', color: '#F43F5E' },
  { id: 'waves', label: 'Abstract Waves', color: '#8B5CF6' },
  { id: 'nebula', label: 'Nebula', color: '#06B6D4' },
  { id: 'geometric', label: 'Geometric', color: '#10B981' },
  { id: 'realistic-photos', label: 'Realistic Photos', color: '#06B6D4' },
  { id: 'cinematic-stock', label: 'Cinematic Stock', color: '#F59E0B' },
  { id: 'nature', label: 'Nature Footage', color: '#10B981' },
  { id: 'urban', label: 'City / Urban', color: '#6366F1' },
  { id: 'documentary', label: 'Documentary', color: '#8B5CF6' },
  { id: 'worship', label: 'Worship / Spiritual', color: '#F59E0B' },
  { id: 'animated-lyrics', label: 'Animated Lyrics', color: '#EC4899' },
  { id: 'captioned-video', label: 'Captioned Video', color: '#8B5CF6' },
  { id: 'motion-typography', label: 'Motion Typography', color: '#6366F1' },
  { id: 'realistic-captions', label: 'Realistic + Captions', color: '#06B6D4' },
  { id: 'realistic-beats', label: 'Realistic + Beat FX', color: '#F43F5E' },
  { id: 'visualizer-cinematic', label: 'Visualizer + Cinematic', color: '#8B5CF6' },
];

const STAGE_LABELS = [
  'Validating source',
  'Importing audio',
  'Analyzing duration / BPM / waveform',
  'Gathering visuals',
  'Generating scenes',
  'Rendering video',
  'Merging audio + captions',
  'Finalizing export',
  'Saving to library',
];

// Stage progress checkpoints — stage 8 (saving) is the ONLY place 100% is set
const STAGE_PROGRESS = [5, 12, 25, 38, 50, 85, 90, 96, 100];

const ABSTRACT_STYLES = new Set(['particles', 'waves', 'nebula', 'geometric']);
const REALISTIC_STYLES = new Set([
  'realistic-photos', 'cinematic-stock', 'nature', 'urban', 'documentary',
  'worship', 'realistic-captions', 'realistic-beats', 'visualizer-cinematic',
]);

// ─── CANVAS DRAW FUNCTIONS ────────────────────────────────────────────────

function drawParticles(ctx, w, h, amplitude, time, particles) {
  ctx.fillStyle = `rgba(0,0,0,0.15)`;
  ctx.fillRect(0, 0, w, h);
  const beat = amplitude > 0.6;
  particles.forEach((p) => {
    p.x += p.vx * (1 + amplitude * 3);
    p.y += p.vy * (1 + amplitude * 3);
    p.life -= 0.008;
    if (p.x < 0 || p.x > w || p.y < 0 || p.y > h || p.life <= 0) {
      p.x = w / 2 + (Math.random() - 0.5) * 200;
      p.y = h / 2 + (Math.random() - 0.5) * 200;
      p.vx = (Math.random() - 0.5) * 4;
      p.vy = (Math.random() - 0.5) * 4;
      p.life = 1;
      p.size = Math.random() * 4 + 1;
      p.hue = Math.random() * 60 + 300;
    }
    const alpha = p.life * 0.8;
    const size = p.size * (1 + amplitude * 2);
    ctx.beginPath();
    ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
    ctx.fillStyle = `hsla(${p.hue + time * 0.5}, 90%, 65%, ${alpha})`;
    ctx.fill();
    if (beat) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, size * 3, 0, Math.PI * 2);
      ctx.fillStyle = `hsla(${p.hue}, 90%, 65%, ${alpha * 0.2})`;
      ctx.fill();
    }
  });
}

function drawWaves(ctx, w, h, amplitude, time, freqData) {
  ctx.fillStyle = `rgba(0,0,0,0.12)`;
  ctx.fillRect(0, 0, w, h);
  const layers = 5;
  for (let l = 0; l < layers; l++) {
    ctx.beginPath();
    const hue = 260 + l * 20 + time * 0.3;
    ctx.strokeStyle = `hsla(${hue}, 80%, 60%, ${0.6 - l * 0.1})`;
    ctx.lineWidth = 2 + l;
    const yBase = h * (0.3 + l * 0.1);
    ctx.moveTo(0, yBase);
    for (let x = 0; x <= w; x += 4) {
      const freqIdx = Math.floor((x / w) * (freqData?.length || 128));
      const freqAmp = freqData ? (freqData[freqIdx] || 0) / 255 : amplitude;
      const y = yBase + Math.sin(x * 0.02 + time * 0.05 + l) * (30 + freqAmp * 80);
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  if (freqData) {
    ctx.beginPath();
    ctx.strokeStyle = `rgba(244,63,94,${0.4 + amplitude * 0.6})`;
    ctx.lineWidth = 2;
    for (let i = 0; i < freqData.length; i++) {
      let x = (i / freqData.length) * w;
      const y = h / 2 + ((freqData[i] / 255) - 0.5) * h * 0.6;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
}

function drawNebula(ctx, w, h, amplitude, time) {
  ctx.fillStyle = `rgba(0,0,0,0.08)`;
  ctx.fillRect(0, 0, w, h);
  const cx = w / 2, cy = h / 2;
  const rings = 8;
  for (let r = 0; r < rings; r++) {
    const radius = (r + 1) * (w / (rings * 2)) * (1 + amplitude * 0.5);
    const hue = (r * 30 + time * 0.5) % 360;
    const grad = ctx.createRadialGradient(cx, cy, radius * 0.5, cx, cy, radius);
    grad.addColorStop(0, `hsla(${hue}, 80%, 60%, ${0.15 + amplitude * 0.2})`);
    grad.addColorStop(1, `hsla(${hue + 40}, 70%, 40%, 0)`);
    ctx.beginPath();
    ctx.arc(cx + Math.sin(time * 0.02 + r) * 30, cy + Math.cos(time * 0.03 + r) * 20, radius, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
  }
  for (let s = 0; s < 60; s++) {
    const sx = (Math.sin(s * 137.5) * 0.5 + 0.5) * w;
    const sy = (Math.cos(s * 137.5) * 0.5 + 0.5) * h;
    const sr = 0.5 + amplitude * 2 * Math.sin(s + time * 0.1);
    ctx.beginPath();
    ctx.arc(sx, sy, Math.max(0.1, sr), 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,255,255,${0.4 + amplitude * 0.6})`;
    ctx.fill();
  }
}

function drawGeometric(ctx, w, h, amplitude, time) {
  ctx.fillStyle = `rgba(0,0,0,0.1)`;
  ctx.fillRect(0, 0, w, h);
  const shapes = 12;
  const cx = w / 2, cy = h / 2;
  for (let i = 0; i < shapes; i++) {
    const angle = (i / shapes) * Math.PI * 2 + time * 0.02;
    const dist = 80 + amplitude * 120 + i * 15;
    let x = cx + Math.cos(angle) * dist;
    const y = cy + Math.sin(angle) * dist;
    const size = 10 + amplitude * 40 + i * 3;
    const hue = (i * 30 + time * 0.5) % 360;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle + time * 0.03);
    ctx.beginPath();
    const sides = 3 + (i % 4);
    for (let s = 0; s < sides; s++) {
      const a = (s / sides) * Math.PI * 2;
      s === 0 ? ctx.moveTo(Math.cos(a) * size, Math.sin(a) * size) : ctx.lineTo(Math.cos(a) * size, Math.sin(a) * size);
    }
    ctx.closePath();
    ctx.strokeStyle = `hsla(${hue}, 80%, 65%, ${0.5 + amplitude * 0.5})`;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = `hsla(${hue}, 80%, 65%, ${0.05 + amplitude * 0.15})`;
    ctx.fill();
    ctx.restore();
  }
  ctx.beginPath();
  ctx.arc(cx, cy, 20 + amplitude * 60, 0, Math.PI * 2);
  ctx.strokeStyle = `rgba(244,63,94,${0.3 + amplitude * 0.7})`;
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawMotionTypography(ctx, w, h, amplitude, time, parsedLines, currentTime) {
  ctx.fillStyle = `rgba(0,0,0,0.15)`;
  ctx.fillRect(0, 0, w, h);
  const cx = w / 2, cy = h / 2;

  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, w * 0.7);
  grad.addColorStop(0, `hsla(${260 + time * 0.2}, 70%, 15%, 0.8)`);
  grad.addColorStop(1, `hsla(${300 + time * 0.1}, 60%, 5%, 0.9)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  if (parsedLines?.length) {
    const currentLine = getCurrentLyricLine(parsedLines, currentTime);
    if (currentLine?.text) {
      const words = currentLine.text.split(' ');
      words.forEach((word, i) => {
        const angle = (i / words.length) * Math.PI * 2 + time * 0.01;
        const dist = 80 + amplitude * 60;
        const wx = cx + Math.cos(angle) * dist;
        const wy = cy + Math.sin(angle) * dist;
        const hue = (i * 40 + time * 0.5) % 360;
        ctx.font = `bold ${18 + amplitude * 12}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillStyle = `hsla(${hue}, 80%, 70%, ${0.6 + amplitude * 0.4})`;
        ctx.fillText(word, wx, wy);
      });
    }
  }

  const pulse = 1 + amplitude * 0.3;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(pulse, pulse);
  ctx.font = `bold ${40 + amplitude * 20}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = `rgba(255,255,255,${0.1 + amplitude * 0.2})`;
  ctx.fillText('♪', 0, 0);
  ctx.restore();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
}

/**
 * Draw realistic style using preloaded scene images.
 * Falls back to cinematic gradient only if NO images loaded at all.
 * Uses Ken Burns effect for smooth motion.
 */
function drawRealisticStyle(ctx, w, h, amplitude, time, styleId, sceneTimeline, currentTime, loadedImages) {
  if (sceneTimeline?.length && loadedImages?.size > 0) {
    // Find current scene
    const currentScene = sceneTimeline.find(
      (s) => currentTime >= s.startTime && currentTime < s.endTime
    ) || sceneTimeline[sceneTimeline.length - 1] || sceneTimeline[0];

    const img = loadedImages.get(currentScene?.index);

    if (img && img.complete && img.naturalWidth > 0) {
      // Ken Burns: slow pan + zoom based on scene progress
      const progress = currentScene.duration > 0
        ? Math.min(1, (currentTime - currentScene.startTime) / currentScene.duration)
        : 0;

      // Alternate pan direction per scene for variety
      const panDir = currentScene.index % 2 === 0 ? 1 : -1;
      const scale = 1 + progress * 0.06;
      const panX = panDir * progress * w * 0.02;
      const offsetX = (w * (scale - 1)) / 2 - panX;
      const offsetY = (h * (scale - 1)) / 2;

      ctx.save();
      ctx.drawImage(img, -offsetX, -offsetY, w * scale, h * scale);
      ctx.restore();

      // Transition fade at scene boundaries
      const timeInScene = currentTime - currentScene.startTime;
      const fadeInDuration = Math.min(0.8, currentScene.duration * 0.1);
      const fadeOutStart = currentScene.duration - fadeInDuration;
      if (timeInScene < fadeInDuration) {
        const fadeAlpha = 1 - (timeInScene / fadeInDuration);
        ctx.fillStyle = `rgba(0,0,0,${fadeAlpha * 0.7})`;
        ctx.fillRect(0, 0, w, h);
      } else if (timeInScene > fadeOutStart) {
        const fadeAlpha = (timeInScene - fadeOutStart) / fadeInDuration;
        ctx.fillStyle = `rgba(0,0,0,${Math.min(0.7, fadeAlpha * 0.7)})`;
        ctx.fillRect(0, 0, w, h);
      }

      // Cinematic letterbox overlay
      const overlay = ctx.createLinearGradient(0, 0, 0, h);
      overlay.addColorStop(0, 'rgba(0,0,0,0.25)');
      overlay.addColorStop(0.4, 'rgba(0,0,0,0)');
      overlay.addColorStop(0.6, 'rgba(0,0,0,0)');
      overlay.addColorStop(1, 'rgba(0,0,0,0.45)');
      ctx.fillStyle = overlay;
      ctx.fillRect(0, 0, w, h);

      // Beat flash on strong beats
      if (amplitude > 0.75) {
        ctx.fillStyle = `rgba(255,255,255,${(amplitude - 0.75) * 0.12})`;
        ctx.fillRect(0, 0, w, h);
      }

      // Scene label (subtle, bottom-left)
      if (currentScene.desc) {
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = 'rgba(255,255,255,0.25)';
        ctx.fillText(currentScene.desc, 16, h - 36);
      }
      return;
    }
  }

  // Fallback: cinematic gradient (only when no images loaded)
  const palettes = {
    'realistic-photos': ['#0a1628', '#1a3a5c', '#2d6a9f'],
    'cinematic-stock': ['#0d0d0d', '#1a0a00', '#3d1a00'],
    'nature': ['#0a1a0a', '#0d3d0d', '#1a6b1a'],
    'urban': ['#050510', '#0a0a2a', '#1a1a4a'],
    'documentary': ['#0d0d0d', '#1a1a1a', '#2a2a2a'],
    'worship': ['#0a0510', '#1a0a2a', '#3d1a5c'],
    'animated-lyrics': ['#050014', '#0a0028', '#1a0050'],
    'captioned-video': ['#000000', '#0a0a0a', '#141414'],
    'motion-typography': ['#050005', '#0a000a', '#1a001a'],
    'realistic-captions': ['#000510', '#000a28', '#001a50'],
    'realistic-beats': ['#100005', '#280010', '#500020'],
    'visualizer-cinematic': ['#050010', '#0a0028', '#1a0050'],
  };
  const pal = palettes[styleId] || palettes['cinematic-stock'];
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, pal[0]);
  grad.addColorStop(0.5, pal[1]);
  grad.addColorStop(1, pal[2]);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Animated light rays
  const cx = w / 2, cy = h * 0.3;
  for (let r = 0; r < 6; r++) {
    const angle = (r / 6) * Math.PI + time * 0.005;
    const len = h * (0.6 + amplitude * 0.4);
    const rayGrad = ctx.createLinearGradient(cx, cy, cx + Math.cos(angle) * len, cy + Math.sin(angle) * len);
    rayGrad.addColorStop(0, `rgba(255,220,150,${0.06 + amplitude * 0.08})`);
    rayGrad.addColorStop(1, 'transparent');
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(angle - 0.05) * len, cy + Math.sin(angle - 0.05) * len);
    ctx.lineTo(cx + Math.cos(angle + 0.05) * len, cy + Math.sin(angle + 0.05) * len);
    ctx.closePath();
    ctx.fillStyle = rayGrad;
    ctx.fill();
  }

  // Floating orbs
  for (let o = 0; o < 5; o++) {
    const ox = w * (0.1 + o * 0.2) + Math.sin(time * 0.01 + o) * 30;
    const oy = h * (0.2 + Math.sin(time * 0.008 + o * 1.3) * 0.3);
    const or = 20 + amplitude * 40 + o * 10;
    const orbGrad = ctx.createRadialGradient(ox, oy, 0, ox, oy, or);
    const hue = (o * 60 + time * 0.2) % 360;
    orbGrad.addColorStop(0, `hsla(${hue}, 70%, 70%, ${0.15 + amplitude * 0.2})`);
    orbGrad.addColorStop(1, 'transparent');
    ctx.beginPath();
    ctx.arc(ox, oy, or, 0, Math.PI * 2);
    ctx.fillStyle = orbGrad;
    ctx.fill();
  }
}

function drawVisualizerCinematic(ctx, w, h, amplitude, time, freqData, sceneTimeline, currentTime, loadedImages) {
  drawRealisticStyle(ctx, w, h, amplitude, time, 'visualizer-cinematic', sceneTimeline, currentTime, loadedImages);
  if (freqData) {
    const barW = w / freqData.length;
    for (let i = 0; i < freqData.length; i++) {
      const barH = (freqData[i] / 255) * (h * 0.3);
      const hue = 340 + i * 0.5;
      ctx.fillStyle = `hsla(${hue}, 80%, 60%, 0.5)`;
      ctx.fillRect(i * barW, h - barH, barW - 0.5, barH);
    }
  }
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────

export default function MusicVideoRenderer({
  audioFile,
  visualStyle = 'particles',
  durationMode = 'full',
  customDuration = 60,
  captionMode = 'none',
  captionStyle = {},
  lyricsText = '',
  videoTitle = '',
  prompt = '',
  mood = '',
  genre = '',
  getEffectiveDuration,
  onVideoReady,
  onProgress,
  onError,
}) {
  const { user, effectiveTier, isPlatformOwner } = useAuth();
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const animFrameRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const particlesRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const timeRef = useRef(0);
  const renderingRef = useRef(false);
  const loadedImagesRef = useRef(new Map());
  const sceneTimelineRef = useRef([]);
  // Track videoId for stable download URL
  const videoIdRef = useRef(null);
  const jobIdRef = useRef(null);

  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [bpm, setBpm] = useState(null);
  const [waveformData, setWaveformData] = useState([]);
  const [currentStageIdx, setCurrentStageIdx] = useState(-1);
  const [errorMsg, setErrorMsg] = useState(null);
  const [sceneCount, setSceneCount] = useState(0);

  const parsedLines = lyricsText?.split('\n')?.filter((l) => l?.trim())?.length
    ? parseLyrics(lyricsText, duration || 180)
    : [];

  const reportStage = useCallback((stageIdx, customPercent) => {
    const pct = customPercent !== undefined ? customPercent : STAGE_PROGRESS[stageIdx];
    setCurrentStageIdx(stageIdx);
    setProgress(pct);
    onProgress?.({
      stage: STAGE_LABELS[stageIdx],
      stageIndex: stageIdx,
      percent: pct,
    });
  }, [onProgress]);

  const initParticles = useCallback((w, h) => {
    particlesRef.current = Array.from({ length: 200 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 3,
      vy: (Math.random() - 0.5) * 3,
      life: Math.random(),
      size: Math.random() * 4 + 1,
      hue: Math.random() * 60 + 300,
    }));
  }, []);

  const startRendering = useCallback(async () => {
    if (!audioFile || !canvasRef.current || renderingRef.current) return;
    renderingRef.current = true;
    setStatus('rendering');
    setProgress(0);
    setVideoUrl(null);
    setErrorMsg(null);
    setCurrentStageIdx(0);
    loadedImagesRef.current = new Map();
    sceneTimelineRef.current = [];

    // Generate stable video ID for this render job
    const videoId = `video-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    videoIdRef.current = videoId;
    jobIdRef.current = null;

    // Guard resource tracking for cleanup in both success and failure paths
    let guardLockId = null;
    let guardCreditLockId = null;

    try {
      // ── Pre-flight: run full generation guard (all safety checks) ──
      reportStage(0);

      const preflightResult = await runGenerationPreflight({
        user,
        userTier: effectiveTier || 'free',
        requestedDurationSeconds: customDuration,
        styleId: visualStyle,
        jobType: 'full_music_video',
      });

      if (!preflightResult.passed) {
        throw new Error(preflightResult.blockReason || 'Generation blocked by safety guard.');
      }

      guardLockId = preflightResult.lockId;

      // Log non-blocking warnings
      preflightResult.warnings?.forEach?.((w) => console.warn('[GenerationGuard]', w));

      // ── Queue job in Supabase (all generation MUST go through queue) ──
      if (user?.id) {
        try {
          const job = await queueGenerationJob({
            userId: user.id,
            jobType: 'full_music_video',
            prompt,
            styleId: visualStyle,
            durationSeconds: customDuration,
            bpm: null,
            mood,
            genre,
            videoTitle,
          });
          jobIdRef.current = job?.id || null;

          // Complete guard setup: link lock to job, lock credits
          const guardSetup = await completeGuardSetup(
            guardLockId,
            user.id,
            job?.id,
            customDuration,
            user.email || ''
          );
          guardCreditLockId = guardSetup.creditLockId;

          // Atomically deduct video seconds (platform owner bypassed inside deductVideoSeconds)
          if (!isPlatformOwner) {
            const deductResult = await deductVideoSeconds(
              user.id, effectiveTier || 'free', customDuration, job?.id, user.email || ''
            );
            if (!deductResult.success) {
              await updateJobProgress(job?.id, { status: 'failed', error_message: deductResult.message });
              throw new Error(deductResult.message || 'Failed to deduct video seconds quota.');
            }

            // Also deduct legacy coins
            await deductCoinsForGeneration(user.id, 'full_music_video', job?.id);
          }
        } catch (jobErr) {
          // Re-throw guard/quota/deduction errors; swallow non-fatal job queue errors
          if (
            jobErr.message?.includes('quota') ||
            jobErr.message?.includes('seconds') ||
            jobErr.message?.includes('plan') ||
            jobErr.message?.includes('deduct') ||
            jobErr.message?.includes('guard') ||
            jobErr.message?.includes('blocked') ||
            jobErr.message?.includes('rate limit') ||
            jobErr.message?.includes('suspended')
          ) {
            throw jobErr;
          }
          console.warn('[MusicVideoRenderer] Job queue error (non-fatal):', jobErr.message);
        }
      }

      await new Promise((r) => setTimeout(r, 400));

      // Stage 1: Importing audio
      reportStage(1);
      await new Promise((r) => setTimeout(r, 300));

      // Stage 2: Analyzing duration / BPM / waveform
      reportStage(2);
      let arrayBuffer;
      if (audioFile?.isUrlImport && audioFile?.url) {
        const resp = await fetch(audioFile.url);
        arrayBuffer = await resp.arrayBuffer();
      } else {
        arrayBuffer = await audioFile.arrayBuffer();
      }
      const analysis = await analyzeAudioBuffer(arrayBuffer);
      setDuration(analysis.duration);
      setWaveformData(analysis.waveform);
      setBpm(analysis.bpm);

      const effectiveDuration = getEffectiveDuration
        ? getEffectiveDuration(analysis.duration)
        : analysis.duration;

      // Create audio URL for playback
      let url;
      if (audioFile?.isUrlImport && audioFile?.url) {
        url = audioFile.url;
      } else {
        url = URL.createObjectURL(audioFile);
      }
      setAudioUrl(url);

      // Update job with BPM
      if (jobIdRef.current) {
        await updateJobProgress(jobIdRef.current, { bpm: analysis.bpm, progress_percent: 25, current_stage: 'gathering_visuals' });
      }

      // Stage 3: Gathering visuals
      reportStage(3);
      const isRealistic = REALISTIC_STYLES.has(visualStyle);
      if (isRealistic) {
        const keywords = extractVisualKeywords({ title: videoTitle, prompt, mood, genre, lyrics: lyricsText });

        // Use AI-analyzed scene prompts if available
        let aiScenePrompts = null;
        try {
          const { scenes } = await analyzeMusicForScenes({
            title: videoTitle,
            genre,
            mood,
            prompt,
            duration: effectiveDuration,
            bpm: analysis.bpm,
            styleId: visualStyle,
          });
          aiScenePrompts = scenes;
        } catch (e) {
          console.warn('[MusicVideoRenderer] AI scene analysis skipped:', e.message);
        }

        // Use BPM-aware Scene Builder for realistic styles
        const timeline = buildSceneTimeline({
          duration: effectiveDuration,
          bpm: analysis.bpm,
          styleId: visualStyle,
          keywords,
        });

        // Override image URLs with AI-generated prompts if available
        if (aiScenePrompts?.length) {
          const batchImages = await generateSceneImageBatch(aiScenePrompts, visualStyle);
          batchImages.forEach((img, i) => {
            if (timeline[i] && img?.imageUrl) {
              timeline[i].imageUrl = img.imageUrl;
            }
          });
        }

        sceneTimelineRef.current = timeline;
        setSceneCount(timeline.length);

        // Preload images with timeout fallback
        const loadedMap = await preloadSceneImages(timeline, 6000);
        loadedImagesRef.current = loadedMap;
      }
      await new Promise((r) => setTimeout(r, 300));

      // Stage 4: Generating scenes
      reportStage(4);
      const canvas = canvasRef.current;
      const W = 1280, H = 720;
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');
      initParticles(W, H);

      ctx.fillStyle = '#050014';
      ctx.fillRect(0, 0, W, H);
      await new Promise((r) => setTimeout(r, 400));

      // Stage 5: Rendering video
      reportStage(5);

      // Setup Web Audio for real-time analysis
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      const audioEl = audioRef.current;
      audioEl.src = url;
      audioEl.crossOrigin = 'anonymous';

      // Setup MediaRecorder
      chunksRef.current = [];
      let stream;
      try {
        stream = canvas.captureStream(30);
      } catch {
        // Fallback: audio-only mode
        reportStage(6);
        await new Promise((r) => setTimeout(r, 200));
        reportStage(7);
        await new Promise((r) => setTimeout(r, 200));
        reportStage(8);
        setProgress(100);
        setStatus('done');
        renderingRef.current = false;
        onVideoReady?.({
          videoId,
          videoUrl: url,
          audioUrl: url,
          duration: effectiveDuration,
          bpm: analysis.bpm,
          waveform: analysis.waveform,
          isAudioOnly: true,
          title: videoTitle,
          visualStyle,
          captionMode,
        });
        return;
      }

      // Add audio track to stream
      const audioDestination = audioCtx.createMediaStreamDestination();
      const source = audioCtx.createMediaElementSource(audioEl);
      source.connect(analyser);
      analyser.connect(audioCtx.destination);
      analyser.connect(audioDestination);
      audioDestination.stream.getAudioTracks().forEach((track) => stream.addTrack(track));

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9' : MediaRecorder.isTypeSupported('video/webm')
        ? 'video/webm' : 'video/mp4';

      const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 4000000 });
      mediaRecorderRef.current = recorder;

      const freqData = new Uint8Array(analyser.frequencyBinCount);

      // CRITICAL: success state ONLY fires inside recorder.onstop — never before
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };

      recorder.onstop = async () => {
        try {
          // Stage 6: Merging audio + captions
          reportStage(6);
          await new Promise((r) => setTimeout(r, 300));

          // Stage 7: Finalizing export
          reportStage(7);
          const blob = new Blob(chunksRef.current, { type: mimeType });

          // Create stable blob URL tied to this videoId
          const vUrl = URL.createObjectURL(blob);
          setVideoUrl(vUrl);
          await new Promise((r) => setTimeout(r, 200));

          // Stage 8: Saving to library — ONLY HERE do we set 100% and 'done'
          reportStage(8);
          // Explicit 100% after reportStage(8) to ensure UI reaches 100
          setProgress(100);
          onProgress?.({
            stage: STAGE_LABELS[8],
            stageIndex: 8,
            percent: 100,
          });
          await new Promise((r) => setTimeout(r, 150));

          setStatus('done');
          renderingRef.current = false;

          // Release guard resources on successful completion
          if (user?.id) {
            releaseGuardResources(user.id, guardLockId, guardCreditLockId, user.email || '').catch(() => {});
          }

          onVideoReady?.({
            videoId,
            videoUrl: vUrl,
            audioUrl: url,
            duration: effectiveDuration,
            bpm: analysis.bpm,
            waveform: analysis.waveform,
            mimeType,
            title: videoTitle,
            visualStyle,
            captionMode,
            sceneTimeline: sceneTimelineRef.current,
          });
        } catch (stopErr) {
          renderingRef.current = false;
          setStatus('error');
          const msg = 'Failed to finalize video: ' + (stopErr?.message || 'Unknown error');
          setErrorMsg(msg);
          onError?.(msg);
        }
      };

      recorder.onerror = (e) => {
        renderingRef.current = false;
        setStatus('error');
        const msg = 'Recording failed: ' + (e?.error?.message || 'Unknown error');
        setErrorMsg(msg);
        onError?.(msg);
      };

      recorder.start(100);

      // Play audio
      await audioEl.play();
      const startTime = performance.now();
      const totalMs = effectiveDuration * 1000;

      const animate = () => {
        if (!renderingRef.current) return;
        analyser.getByteFrequencyData(freqData);
        const avg = freqData.reduce((a, b) => a + b, 0) / freqData.length;
        const amplitude = avg / 255;
        timeRef.current++;

        const elapsed = performance.now() - startTime;
        const currentAudioTime = elapsed / 1000;

        // Draw visual style
        switch (visualStyle) {
          case 'waves':
            drawWaves(ctx, W, H, amplitude, timeRef.current, freqData);
            break;
          case 'nebula':
            drawNebula(ctx, W, H, amplitude, timeRef.current);
            break;
          case 'geometric':
            drawGeometric(ctx, W, H, amplitude, timeRef.current);
            break;
          case 'motion-typography': case'animated-lyrics':
            drawMotionTypography(ctx, W, H, amplitude, timeRef.current, parsedLines, currentAudioTime);
            break;
          case 'visualizer-cinematic':
            drawVisualizerCinematic(ctx, W, H, amplitude, timeRef.current, freqData, sceneTimelineRef.current, currentAudioTime, loadedImagesRef.current);
            break;
          case 'particles':
            drawParticles(ctx, W, H, amplitude, timeRef.current, particlesRef.current);
            break;
          default:
            // All realistic styles use scene-based rendering
            drawRealisticStyle(ctx, W, H, amplitude, timeRef.current, visualStyle, sceneTimelineRef.current, currentAudioTime, loadedImagesRef.current);
        }

        // Waveform bar at bottom for abstract styles
        if (ABSTRACT_STYLES.has(visualStyle)) {
          const barW = W / freqData.length;
          for (let i = 0; i < freqData.length; i++) {
            const barH = (freqData[i] / 255) * 80;
            ctx.fillStyle = `rgba(244,63,94,${0.4 + amplitude * 0.6})`;
            ctx.fillRect(i * barW, H - barH, barW - 1, barH);
          }
        }

        // Caption overlay
        if (captionMode !== 'none' && parsedLines.length > 0) {
          drawCaptionOverlay(ctx, W, H, parsedLines, currentAudioTime, captionMode, captionStyle);
        }

        // LoopNest watermark
        ctx.fillStyle = 'rgba(255,255,255,0.2)';
        ctx.font = 'bold 13px sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';
        ctx.fillText('LoopNest AI', 16, H - 16);

        // Progress during rendering: stage 5 goes from 50% to 84%
        // NEVER reaches 85+ until recorder.onstop fires
        const renderPct = Math.min(84, 50 + (elapsed / totalMs) * 34);
        setProgress(renderPct);
        onProgress?.({
          stage: STAGE_LABELS[5],
          stageIndex: 5,
          percent: renderPct,
        });

        const shouldStop = elapsed >= totalMs || audioEl.ended;
        if (shouldStop) {
          audioEl.pause();
          cancelAnimationFrame(animFrameRef.current);
          if (recorder.state === 'recording') recorder.stop();
        } else {
          animFrameRef.current = requestAnimationFrame(animate);
        }
      };

      animFrameRef.current = requestAnimationFrame(animate);

      // Safety timeout: stop after totalMs + 8s
      setTimeout(() => {
        if (renderingRef.current && recorder.state === 'recording') {
          audioEl.pause();
          cancelAnimationFrame(animFrameRef.current);
          recorder.stop();
        }
      }, totalMs + 8000);

    } catch (err) {
      // Refund video seconds and coins on failure
      if (user?.id && !isPlatformOwner && jobIdRef.current) {
        try {
          await refundVideoSeconds(user.id, customDuration, jobIdRef.current, user.email || '');
          await refundCoinsForFailedJob(user.id, 'full_music_video', jobIdRef.current);
        } catch (refundErr) {
          console.warn('[MusicVideoRenderer] Refund failed:', refundErr.message);
        }
      }
      if (jobIdRef.current) {
        await updateJobProgress(jobIdRef.current, { status: 'failed', error_message: err.message });
      }
      // Release guard resources (generation lock + credit lock)
      if (user?.id) {
        await releaseGuardResources(user.id, guardLockId, guardCreditLockId, user.email || '');
      }
      renderingRef.current = false;
      setStatus('error');
      setErrorMsg(err?.message || 'Rendering failed');
      onError?.(err?.message || 'Rendering failed');
    }
  }, [audioFile, visualStyle, durationMode, customDuration, lyricsText, videoTitle, prompt, mood, genre, getEffectiveDuration, onVideoReady, onProgress, onError, initParticles, reportStage, user, effectiveTier, isPlatformOwner]);

  const handleReset = useCallback(() => {
    renderingRef.current = false;
    cancelAnimationFrame(animFrameRef.current);
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    if (audioCtxRef.current?.state !== 'closed') audioCtxRef.current?.close();
    setStatus('idle');
    setProgress(0);
    setVideoUrl(null);
    setErrorMsg(null);
    setCurrentStageIdx(-1);
    setBpm(null);
    setWaveformData([]);
    setSceneCount(0);
    videoIdRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      renderingRef.current = false;
      cancelAnimationFrame(animFrameRef.current);
      if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
      if (audioCtxRef.current?.state !== 'closed') audioCtxRef.current?.close();
    };
  }, []);

  const styleInfo = VISUAL_STYLES.find((s) => s.id === visualStyle);

  return (
    <div className="flex flex-col gap-3">
      {/* Hidden audio element */}
      <audio ref={audioRef} style={{ display: 'none' }} />

      {/* Canvas preview */}
      <div className="relative rounded-xl overflow-hidden" style={{ background: '#000', aspectRatio: '16/9' }}>
        <canvas
          ref={canvasRef}
          className="w-full h-full object-contain"
          style={{ display: status === 'idle' ? 'none' : 'block' }}
        />
        {status === 'idle' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(244,63,94,0.2)' }}>
              <Icon name="Film" size={24} color="#F43F5E" />
            </div>
            <p className="text-sm text-white/60">Canvas preview will appear here</p>
          </div>
        )}
        {status === 'rendering' && (
          <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: 'rgba(0,0,0,0.7)' }}>
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#F43F5E' }} />
            <span className="text-xs text-white/80">Recording...</span>
          </div>
        )}
        {status === 'done' && videoUrl && (
          <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: 'rgba(16,185,129,0.8)' }}>
            <Icon name="CheckCircle2" size={12} color="white" />
            <span className="text-xs text-white">Ready</span>
          </div>
        )}
      </div>

      {/* Analysis info */}
      {bpm && (
        <div className="flex gap-3 text-xs flex-wrap" style={{ color: 'var(--color-muted-foreground)' }}>
          <span>⏱ Duration: {formatDurationLabel(duration)}</span>
          <span>🎵 BPM: ~{bpm}</span>
          <span>🎨 Style: {styleInfo?.label || visualStyle}</span>
          {sceneCount > 0 && (
            <span>🎬 Scenes: {sceneCount}</span>
          )}
        </div>
      )}

      {/* Generate button */}
      {status === 'idle' && audioFile && (
        <button
          onClick={startRendering}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
          style={{ background: 'linear-gradient(135deg, #F43F5E, #8B5CF6)', color: 'white' }}
        >
          <Icon name="Wand2" size={16} color="white" />
          Generate Music Video
        </button>
      )}

      {/* Real video preview after done */}
      {status === 'done' && videoUrl && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-caption)' }}>✓ Playable video ready — {formatDurationLabel(duration)}</p>
          <video
            src={videoUrl}
            controls
            className="w-full rounded-xl"
            style={{ maxHeight: 200, background: '#000' }}
          />
          <div className="flex gap-2">
            <a
              href={videoUrl}
              download={`${videoTitle || 'music-video'}-${videoIdRef.current || 'export'}.webm`}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
              style={{ background: 'rgba(16,185,129,0.1)', color: 'var(--color-success)', border: '1px solid rgba(16,185,129,0.3)' }}
            >
              <Icon name="Download" size={13} color="var(--color-success)" />
              Download Video
            </a>
            <button
              onClick={handleReset}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
              style={{ background: 'rgba(244,63,94,0.1)', color: '#F43F5E', border: '1px solid rgba(244,63,94,0.3)' }}
            >
              <Icon name="RefreshCw" size={13} color="#F43F5E" />
              Re-render
            </button>
          </div>
        </div>
      )}

      {/* Error state */}
      {status === 'error' && (
        <div className="flex items-start gap-2 p-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
          <Icon name="AlertCircle" size={16} color="#EF4444" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-semibold" style={{ color: '#EF4444' }}>Rendering failed</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>{errorMsg || 'An error occurred. Please try again.'}</p>
          </div>
          <button
            onClick={handleReset}
            className="ml-auto text-xs px-2 py-1 rounded-lg flex-shrink-0"
            style={{ background: 'rgba(239,68,68,0.2)', color: '#EF4444' }}
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
